import { put, call, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { emailTemplatesActions } from '../redux/emailTemplatesSlice';
import { emailTemplatesSelectors } from '../redux/selectors';
import {
    fetchEmailTemplatesApi,
    createEmailTemplateApi,
    updateEmailTemplateApi,
    deleteEmailTemplateApi,
    publishEmailTemplateApi,
    fetchEmailTemplateLabelsApi,
    previewEmailTemplateApi,
} from '../../apis/emailTemplates';
import {
    SnackbarUtils,
    IEmailTemplate,
    IEmailTemplateLabel,
    IEmailTemplateListResponse,
    IEmailTemplatesFilters,
    IFetchEmailTemplatesParams,
    ICreateEmailTemplatePayload,
    IPreviewTemplatePayload,
    IPreviewTemplateResponse,
    IUpdateEmailTemplatePayload,
} from '../../../common';

const isSuccessStatus = (status?: number) =>
    status !== undefined && status >= 200 && status < 300;

export function* fetchTemplatesSaga() {
    try {
        const page: number = yield select(
            emailTemplatesSelectors.getCurrentPage
        );
        const pageSize: number = yield select(
            emailTemplatesSelectors.getPageSize
        );
        const search: string = yield select(emailTemplatesSelectors.getSearch);
        const filters: IEmailTemplatesFilters = yield select(
            emailTemplatesSelectors.getFilters
        );
        const trimmedSearch = search.trim();
        const params: IFetchEmailTemplatesParams = {
            page,
            page_size: pageSize,
            ...(trimmedSearch ? { search: trimmedSearch } : {}),
            ...(filters.type ? { template_type: filters.type } : {}),
            ...(filters.status ? { status: filters.status } : {}),
        };
        const { response } = yield call(fetchEmailTemplatesApi, params);
        if (isSuccessStatus(response?.status)) {
            const data: IEmailTemplateListResponse = response.data;
            yield put(
                emailTemplatesActions.setTemplatesData({
                    templates: data.results || [],
                    totalPages: data.total_pages || 1,
                    totalCount: data.total_count || 0,
                    currentPage: data.current_page || 1,
                })
            );
        } else {
            yield put(emailTemplatesActions.setTemplatesError());
            yield call(
                SnackbarUtils.error,
                i18next.t('settings.emailTemplates.toast.loadError')
            );
        }
    } catch (error) {
        console.error('Fetch email templates error:', error);
        yield put(emailTemplatesActions.setTemplatesError());
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}

export function* fetchPickerOptionsSaga() {
    try {
        const { response } = yield call(fetchEmailTemplateLabelsApi);
        if (isSuccessStatus(response?.status)) {
            const data: Array<IEmailTemplateLabel> = response.data;
            yield put(emailTemplatesActions.setPickerOptions(data || []));
        } else {
            yield put(emailTemplatesActions.setPickerOptionsError());
            yield call(
                SnackbarUtils.error,
                i18next.t('settings.emailTemplates.toast.loadError')
            );
        }
    } catch (error) {
        console.error('Fetch email template options error:', error);
        yield put(emailTemplatesActions.setPickerOptionsError());
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}

export function* applyTemplateSaga(
    action: PayloadAction<IPreviewTemplatePayload>
) {
    try {
        const { response } = yield call(
            previewEmailTemplateApi,
            action.payload
        );
        if (isSuccessStatus(response?.status)) {
            const data: IPreviewTemplateResponse = response.data;
            yield put(
                emailTemplatesActions.setRenderedTemplate({
                    subject: data.subject || '',
                    body: data.body || '',
                })
            );
        } else {
            yield put(emailTemplatesActions.setApplyTemplateError());
            yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
        }
    } catch (error) {
        console.error('Preview email template error:', error);
        yield put(emailTemplatesActions.setApplyTemplateError());
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    }
}

export function* createTemplateSaga(
    action: PayloadAction<{
        template: ICreateEmailTemplatePayload;
        publish?: boolean;
    }>
) {
    try {
        const { template, publish } = action.payload;
        const { response } = yield call(createEmailTemplateApi, template);
        if (isSuccessStatus(response?.status)) {
            const createdId: number | undefined = response?.data?.id;
            if (publish && createdId) {
                const { response: publishResponse } = yield call(
                    publishEmailTemplateApi,
                    createdId
                );
                yield call(
                    isSuccessStatus(publishResponse?.status)
                        ? SnackbarUtils.success
                        : SnackbarUtils.error,
                    i18next.t(
                        isSuccessStatus(publishResponse?.status)
                            ? 'settings.emailTemplates.toast.published'
                            : 'errors.generalError'
                    )
                );
            } else {
                yield call(
                    SnackbarUtils.success,
                    i18next.t('settings.emailTemplates.toast.saved')
                );
            }
            yield put(emailTemplatesActions.fetchTemplates());
        } else {
            yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
        }
    } catch (error) {
        console.error('Create email template error:', error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(emailTemplatesActions.setSaveTemplateDone());
    }
}

export function* updateTemplateSaga(
    action: PayloadAction<{
        template: IUpdateEmailTemplatePayload;
        publish?: boolean;
    }>
) {
    try {
        const { template, publish } = action.payload;
        const { id, ...fields } = template;
        if (publish) {
            const { response } = yield call(
                publishEmailTemplateApi,
                id,
                fields
            );
            yield call(
                isSuccessStatus(response?.status)
                    ? SnackbarUtils.success
                    : SnackbarUtils.error,
                i18next.t(
                    isSuccessStatus(response?.status)
                        ? 'settings.emailTemplates.toast.published'
                        : 'errors.generalError'
                )
            );
        } else {
            const { response } = yield call(updateEmailTemplateApi, template);
            yield call(
                isSuccessStatus(response?.status)
                    ? SnackbarUtils.success
                    : SnackbarUtils.error,
                i18next.t(
                    isSuccessStatus(response?.status)
                        ? 'settings.emailTemplates.toast.saved'
                        : 'errors.generalError'
                )
            );
        }
        yield put(emailTemplatesActions.fetchTemplates());
    } catch (error) {
        console.error('Update email template error:', error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(emailTemplatesActions.setSaveTemplateDone());
    }
}

export function* deleteTemplateSaga(action: PayloadAction<number>) {
    try {
        const { response } = yield call(
            deleteEmailTemplateApi,
            action.payload
        );
        if (isSuccessStatus(response?.status)) {
            yield call(
                SnackbarUtils.success,
                i18next.t('settings.emailTemplates.toast.deleted')
            );
            const templates: Array<IEmailTemplate> = yield select(
                emailTemplatesSelectors.getAllTemplates
            );
            const currentPage: number = yield select(
                emailTemplatesSelectors.getCurrentPage
            );
            if (templates.length <= 1 && currentPage > 1) {
                yield put(
                    emailTemplatesActions.setCurrentPage(currentPage - 1)
                );
            } else {
                yield put(emailTemplatesActions.fetchTemplates());
            }
        } else {
            yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
        }
    } catch (error) {
        console.error('Delete email template error:', error);
        yield call(SnackbarUtils.error, i18next.t('errors.generalError'));
    } finally {
        yield put(emailTemplatesActions.setDeleteTemplateDone());
    }
}
